import { useDraggable } from "@dnd-kit/core";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  getDebugInfoText,
  getMDIcon,
  getMdOptions,
  getTitleData,
} from "./helpers";
import {
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { setSelectedCarouselIndex } from "redux/Modals/actions";
import {
  clearImagePageParamsContext,
  setClusterPageScrollPosition,
  setDataPageScrollPosition,
  setNavigationCluster,
  setSimilarityData,
} from "redux/SingleDataset/actions";

import { amplitudeTrack } from "helpers/utility/amplitude";
import {
  AMP_DATASET_EXPLORATION_EVENT__ENTITY__OPENED,
  AMP_DATASET_EXPLORATION_EVENT__VISUAL_SEARCH__APPLIED,
} from "helpers/constants/amplitudeEvents";
import {
  AMP_DATASET_EXPLORATION_PROPERTY__CLUSTER_SIZE,
  AMP_DATASET_EXPLORATION_PROPERTY__CONTEXT,
  AMP_DATASET_EXPLORATION_PROPERTY__TARGET,
} from "helpers/constants/amplitudeProperties";

import {
  addQueryParameter,
  getInitialMDType,
  getMediaDimensions,
  useWindowResize,
} from "helpers/utility/utilities";
import { DATASET_CREATION_PAGE, ENRICH_PAGE } from "helpers/constants/pages";
import {
  DATA_PAGE_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  LABEL_CLASS_PARAMETER,
  LABELS_PARAMETER,
  LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
  METADATA_SOURCES_PARAMETER,
  OBJECT_ID_QUERY_PARAMETER,
  PAGE_PARAMETER,
  REVIEW_PARAMETER,
  VIDEO_CLUSTER_ID_PARAMETER,
  VIDEO_ID_PARAMETER,
} from "helpers/constants/miscellaneous";
import {
  CardBodyImage,
  CardTypes,
  CardVariant,
  DataPreview,
  EventTarget,
  HandleSelect,
  isValidEntityType,
  MetadataType,
  RelevanceType,
  SetStateFunction,
  SingleExportItem,
} from "types";
import { AnnotatedBoundingBox } from "extractedBackendTypes";

import AssetPreviewCard from "views/components/AssetPreviewCard";
import ExcludeIcon from "assets/icons/ExcludeIcon";
import CheckIcon from "assets/icons/CheckIcon";
import XIcon from "assets/icons/XIcon";
import CardHeader from "./CardHeader";
import CardBody from "./CardBody";

import styles from "./style.module.scss";
import classNames from "classnames";
import useQueryParam from "hooks/useQueryParam";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { VQL_EXCLUDE } from "helpers/constants/filters";
import ReviewCardHeader from "./ReviewCardHeader";
import useVqlParams, { type VqlListMutators } from "hooks/useVqlParams";
import ImageIcon from "assets/icons/imageIcon";
import { Cluster, PreviewBorderColor } from "../index";
import VLFloatGroup from "views/uikit/VLFloatGroup";
import ReviewFloatingDetails from "../../../../../components/LabelPropagation/ReviewFloatingDetails";
import { getVLColorByActionColor } from "../../ReviewList/utils";
import ReloadIcon from "assets/icons/ReloadIcon";
import { getDatasetContextualLabels } from "redux/SingleDataset/selectors";
import { VQL_UPGRADE, CAPTIONS_FILTER } from "helpers/constants/filters";
import { useSearchByRelevanceEnabled } from "hooks/useFeatureFlagHooks";
import VisualSimilarityPopover from "views/components/VisualSimilarityPopover";
import intersection from "lodash/intersection";
import { EnrichmentCardProvider } from "./EnrichmentCardProvider";
import isEmpty from "lodash/isEmpty";
import { VisualSimilarityIcon } from "../../DataPageFilters/SearchContainer/Icons";
import { Tooltip } from "antd";
import {
  selectedLabelTextsFromVql,
  selectedUserTagTextsFromVql,
  toggleVqlLabelsIsValue,
  toggleVqlUserTagsIsValue,
  useCardFilters,
  vqlHasUserTagInIsFilter,
} from "./useCardFilters";

export interface EnrichmentCardProps {
  page: string;
  index: number;
  singleData: any;
  datasetId: string;
  clusterID: string;
  isVertex: boolean;
  isDataPage: boolean;
  cardElementRef?: any;
  isClusterPage: boolean;
  isVertexCard?: boolean;
  indexOfPointerOut: number;
  showSelectOption?: boolean;
  showMetadataSummary: boolean;
  modelType?: "IMAGE" | "OBJECT";
  globalMDType: MetadataType | null;
  containerRef: RefObject<HTMLDivElement>;

  clusters?: any[];
  showDebugInfo?: boolean;
  exportData?: SingleExportItem[];
  relevanceScores?: number[] | null;
  navigationCluster?: any;

  onSelect: HandleSelect;
  onRemoveVertex?: (e: MouseEvent) => void;
  onIndexOfPointerOut: SetStateFunction<number>;
  onGlobalMDTypeChange: (type: MetadataType) => void;
  onSetVertex?: (
    id: string,
    type: "MEDIA" | "CLUSTER",
    isExclude?: boolean,
    mediaData?: any,
  ) => void | Promise<void>;
  onSetVertexSimilarity?: (threshold: string) => void;
  cardTitle?: string;
  showFeedbackInCard?: boolean;
  disableInteractivity?: boolean;
  className?: string;
  columnsCount?: number;
  selectable?: boolean;
}

//temporary added the flag to disable visual similarity threshold feature
const isVisualSimilarityEnabled = false;

const EnrichmentCard = (props: EnrichmentCardProps) => {
  const {
    index,
    page,
    isVertex,
    clusterID,
    modelType,
    isDataPage,
    singleData,
    containerRef,
    globalMDType,
    isClusterPage,
    showSelectOption,
    indexOfPointerOut,
    showMetadataSummary,

    clusters = [],
    exportData = [],
    showDebugInfo = false,
    relevanceScores = null,
    navigationCluster,

    onSelect,
    onSetVertex,
    onSetVertexSimilarity,
    onRemoveVertex,
    onIndexOfPointerOut,
    onGlobalMDTypeChange,
    cardTitle,
    showFeedbackInCard,
    disableInteractivity = false,
    className,
    columnsCount,
    selectable = true,
  } = props;
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: index,
    data: {
      ...singleData,
      exportType: isDataPage ? "cluster" : "media",
      props,
      trigger: "drag",
    },
  });

  const isVideoPageV2Enabled = useFeatureFlagService(VQL_UPGRADE).enabled;
  const isVqlExcludeEnabled = useFeatureFlagService(VQL_EXCLUDE).enabled;

  const [isHovering, setIsHovering] = useState<any>(false);
  const [mdLabels, setMdLabels] = useState<string[] | null>(null);
  const { addVqlQuery, vqlQueries, updateVqlQuery, removeVqlQuery } =
    useVqlParams();

  const entitySearchParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityParams = isValidEntityType(entitySearchParam)
    ? entitySearchParam
    : null;

  const queryParamsFilters = useAllQueryParamsFilters();
  const fetchDatasetUserTagsCatalog = globalMDType === MetadataType.USER_TAGS;

  const { userTags: datasetUserTagsCatalog, resolveUserTagIdByName } =
    useCardFilters(fetchDatasetUserTagsCatalog);
  const [hoveredAssetData, setHoveredAssetData] = useState({
    id: "",
    dimensions: { naturalHeight: 0, naturalWidth: 0 },
    error: false,
  });
  const { datasetId, clusterId, enrichDatasetId, newDatasetId } = useParams();
  const assetPreviewFlagRef = useRef(true);
  const { width } = useWindowResize();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isSearchByRelevanceEnabled = useSearchByRelevanceEnabled();
  const isCaptionsVisible = useFeatureFlagService(CAPTIONS_FILTER).enabled;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const uniqueClusterWrapperId = singleData.__id;

  const isDataCreationPage = page === DATASET_CREATION_PAGE;
  const isEnrichmentPage = page === ENRICH_PAGE;

  const isObjectModel = modelType === "OBJECT";
  const bodyEntityType: CardTypes = singleData.type;
  const isObjectCard = bodyEntityType === "OBJECT";
  const inReview = searchParams.has(REVIEW_PARAMETER);
  const inReviewDetails = searchParams.has(LABEL_CLASS_PARAMETER);
  const isPreview = singleData?.cluster_id;

  const showFloatingDetails = !!singleData.floatingDetails && inReviewDetails;
  const hideMetadataOptions =
    isEnrichmentPage || isDataCreationPage || disableInteractivity;
  const showActionButtons = isHovering && !hideMetadataOptions;

  const isBodyUnclickable = !!enrichDatasetId || isVertex || !!newDatasetId;

  const isSingleEntityOnDataPage =
    isDataPage && singleData.previews.length === 1;

  const firstPreview = singleData.previews?.[0];

  const fileName = hideMetadataOptions
    ? singleData.file_name ||
      singleData.original_uri ||
      firstPreview?.file_name ||
      firstPreview?.original_uri
    : isSingleEntityOnDataPage
      ? firstPreview.video_uri
        ? firstPreview.video_uri.split("/")[
            firstPreview.video_uri.split("/").length - 1
          ]
        : firstPreview.file_name
      : isClusterPage
        ? singleData.video_uri
          ? singleData.video_uri.split("/")[
              singleData.video_uri.split("/").length - 1
            ]
          : singleData.file_name
        : "";

  const formattedFilename = decodeURIComponent(fileName);

  const getEnrichPageLabels = (): string[] => {
    if (isObjectCard) {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.OBJECT_LABELS:
        case MetadataType.USER_TAGS:
          return singleData.labels || [];
        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          return singleData.caption ? [singleData.caption] : [];
        default:
          return [];
      }
    } else {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.USER_TAGS:
          return singleData.labels || [];

        case MetadataType.OBJECT_LABELS:
          return singleData.objects
            .map((obj: any) => obj.labels?.[0])
            .filter((label: any): label is string => !!label);

        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          return isObjectModel
            ? singleData.objects
                .map((obj: any) => obj.caption)
                .filter((caption: any): caption is string => !!caption)
            : singleData.caption
              ? [singleData.caption]
              : [];

        default:
          return [];
      }
    }
  };

  const getCreationPageLabels = (): string[] => {
    const preview: DataPreview = singleData;
    let objectAnnotations = preview.object_annotations || [];
    let imageAnnotations = preview.image_annotations || [];

    if (!!objectAnnotations.length) {
      return objectAnnotations
        .map((singleAnnotation) =>
          singleAnnotation.annotations ? singleAnnotation.annotations[0] : null,
        )
        .filter((item): item is string => typeof item === "string");
    }

    return imageAnnotations;
  };

  const getCreationBoundingBoxes = (): number[][] => {
    let objectAnnotations: AnnotatedBoundingBox[] =
      singleData.object_annotations || [];

    if (!!objectAnnotations.length) {
      return objectAnnotations
        .map((annotation) => {
          let { col_x, row_y, width, height } = annotation;
          if (col_x && row_y && width && height) {
            return [col_x, row_y, width, height];
          } else return [];
        })
        .slice(0, 5);
    } else {
      return [];
    }
  };

  const getEnrichmentBoundingBoxes = (): number[][] => {
    if (isObjectCard) {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.USER_TAGS:
          return [];

        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.OBJECT_LABELS:
        case MetadataType.IMAGE_CAPTIONS:
          return singleData.bounding_box ? [singleData.bounding_box] : [];
        default:
          return [];
      }
    } else {
      switch (globalMDType) {
        case MetadataType.IMAGE_LABELS:
        case MetadataType.USER_TAGS:
          return [];

        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.OBJECT_LABELS:
        case MetadataType.IMAGE_CAPTIONS:
          return singleData.objects
            .map((obj: any) => obj.bounding_box)
            .filter((label: any): label is number[] => !!label)
            .slice(0, 5);

        default:
          return [];
      }
    }
  };

  const getDataStrings = (
    data: any,
  ): {
    labels: string[];
    userTags: string[];
    captions: string[];
  } => {
    if (hideMetadataOptions || inReview) {
      return { labels: [], userTags: [], captions: [] };
    }

    if (hoveredAssetData.id) {
      const assetData = getAssetData();
      return {
        labels: assetData.labels || [],
        userTags: assetData.user_tags || [],
        captions: assetData.caption ? [assetData.caption] : [],
      };
    }

    return {
      labels: data.labels,
      userTags: data.user_tags,
      captions: data.captions,
    };
  };

  const { labels, userTags, captions } = useMemo(
    () => getDataStrings(singleData),
    //eslint-disable-next-line
    [singleData, hoveredAssetData],
  );
  const mdType = useMemo(
    () =>
      getInitialMDType(
        globalMDType,
        userTags,
        labels,
        captions,
        entityParams,
        hideMetadataOptions,
      ),
    //eslint-disable-next-line
    [globalMDType],
  );
  const [cardMDType, setCardMDType] = useState<MetadataType>(mdType);

  //using a specific index for cart selections
  const specificIndex = `id-${
    singleData.cluster_id ? singleData.cluster_id : singleData.media_id
  }-index-${index}`;

  const isObjectOnEnrichmentPage = isEnrichmentPage && modelType === "OBJECT";
  const isSingleObject = isDataPage
    ? false
    : isObjectOnEnrichmentPage ||
      singleData.type === "OBJECT" ||
      globalMDType === MetadataType.OBJECT_LABELS;

  const boundingBox = isDataCreationPage
    ? getCreationBoundingBoxes()
    : isObjectOnEnrichmentPage
      ? getEnrichmentBoundingBoxes()
      : isSingleObject
        ? [singleData.bounding_box]
        : null;

  const dynamicAssetPreviewEnabled =
    (indexOfPointerOut === -1 || indexOfPointerOut === index) &&
    !disableInteractivity;

  const hoverDelay = dynamicAssetPreviewEnabled ? 700 : 0;

  const isSelected = useMemo(() => {
    if (singleData.flat_response) {
      return exportData.some(
        (data: any) => data.media_id === singleData.previews[0].media_id,
      );
    } else if (isDataPage) {
      return !!exportData.find(
        (singleExp: any) => singleExp.cluster_id === singleData.cluster_id,
      );
    } else if (isClusterPage) {
      const selectedParentCluster = exportData.find(
        (data: any) =>
          data.cluster_id === clusterID && data.exportType === "cluster",
      );
      if (selectedParentCluster) {
        //check if the media is selected to be removed from cluster selection
        const selectedForRemoval = selectedParentCluster.excludeMediaIDs
          ? selectedParentCluster.excludeMediaIDs.includes(singleData.media_id)
          : false;
        if (selectedForRemoval) {
          return false;
        }
        return true;
      } else {
        return !!exportData.find(
          (data: any) => data.media_id === singleData.media_id,
        );
      }
    } else if (inReview && inReviewDetails) {
      return !!exportData.find(
        (data: any) => data.media_id === singleData.media_id,
      );
    }
    return false;
    //eslint-disable-next-line
  }, [exportData, isDataPage, isClusterPage]);

  const cardVariant: CardVariant = isVertex
    ? CardVariant.VERTEX
    : isSelected
      ? CardVariant.SELECTED
      : CardVariant.DEFAULT;

  let finalTitle: string | JSX.Element;
  let titleIcon: JSX.Element;

  if (cardTitle) {
    finalTitle = cardTitle;
    titleIcon = <ImageIcon />;
  } else {
    const titleData = getTitleData(
      singleData,
      clusterId,
      datasetId,
      formattedFilename,
      isSelected,
      isVertex,
    );
    finalTitle = titleData.title;
    titleIcon = titleData.titleIcon;
  }

  const cardClassNames = classNames(styles.dataCard, className, {
    [styles.dataCardWithOutline]: isHovering,
    [styles.dataCardForSimilarity]: isVertex,
    [styles.dataCardForExport]: showSelectOption && isSelected && !isVertex,
  });

  const floatingDetailsClassnames = classNames(styles.floatingDetails, {
    [styles.selectedFloatingDetails]:
      singleData.floatingDetails !== "For Review",
  });

  const getRelevanceType = useCallback(
    (relevance: number) => {
      let relevanceType: RelevanceType | null = null;

      if (!relevanceScores) {
        return null;
      }

      if (relevanceScores.length === 3) {
        const H = relevanceScores[0];
        const M = relevanceScores[1];
        const L = relevanceScores[2];

        if (relevance >= 0 && relevance <= H) {
          relevanceType = "High";
        } else if (relevance > H && relevance <= M) {
          relevanceType = "Medium";
        } else if (relevance > M && relevance <= L) {
          relevanceType = "Low";
        }
      }
      return relevanceType;
    },
    [relevanceScores],
  );

  const getRelevance = useCallback(
    (preview: any) => {
      if (
        !relevanceScores ||
        preview.relevance_score_type === "preview_order"
      ) {
        return null;
      }
      const relevanceType = getRelevanceType(preview.relevance_score || -1);
      const zone = preview.relevance_zone;
      return {
        type: zone ? null : relevanceType,
        score: preview.relevance_score,
        zone,
      };
    },
    [getRelevanceType, relevanceScores],
  );

  const getDynamicImages = useCallback(
    (type: "plain" | "thumb", previews: any) => {
      previews = previews?.length ? previews : [];
      return previews.map((preview: any) => {
        return {
          id: preview.media_id,
          relevance: getRelevance(preview),
          timestamp: preview.frame_timestamp,
          src: type === "plain" ? preview.media_uri : preview.media_thumb_uri,
        };
      }) as CardBodyImage[];
    },
    [getRelevance],
  );

  const plainImages = useMemo(
    () => getDynamicImages("plain", singleData.previews),
    [getDynamicImages, singleData.previews],
  );

  const thumbImages = useMemo(
    () => getDynamicImages("thumb", singleData.previews),
    [getDynamicImages, singleData.previews],
  );

  const plainImage: CardBodyImage = {
    id: singleData.media_id,
    src: singleData.media_uri,
    relevance: getRelevance(singleData),
    timestamp: singleData.frame_timestamp,
  };
  const thumbImage: CardBodyImage = {
    id: singleData.media_id,
    src: singleData.media_thumb_uri,
    relevance: getRelevance(singleData),
    timestamp: singleData.frame_timestamp,
  };

  function getAssetData() {
    let assetData;

    if (hoveredAssetData.id) {
      if (isDataPage) {
        assetData = singleData.previews.find(
          (preview: any) => preview.media_id === hoveredAssetData.id,
        );
      } else {
        assetData =
          singleData.media_id === hoveredAssetData.id ? singleData : undefined;
      }
    }

    return assetData ?? {};
  }

  const setAssetPreviewFlag = (value: boolean) => {
    assetPreviewFlagRef.current = value;
  };

  const clearAssetData = () => {
    setHoveredAssetData({
      id: "",
      dimensions: { naturalHeight: 0, naturalWidth: 0 },
      error: false,
    });
  };

  const updateAssetData = async (id: string, source: string) => {
    if (inReview) {
      return;
    }
    const dimensions = {
      naturalWidth: LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
      naturalHeight: LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
    };

    try {
      const res = await getMediaDimensions(source);
      const result = { id, dimensions: res, error: false };

      if (assetPreviewFlagRef.current) {
        setHoveredAssetData((prev) => {
          const oldHeight = prev.dimensions.naturalHeight;
          const oldWidth = prev.dimensions.naturalWidth;

          const newHeight = res.naturalHeight;
          const newWidth = res.naturalWidth;

          // Normalize dimensions by the larger dimension to ensure consistent scale
          const normalizedOldHeight = oldHeight / Math.max(oldHeight, oldWidth);
          const normalizedOldWidth = oldWidth / Math.max(oldHeight, oldWidth);
          const normalizedNewHeight = newHeight / Math.max(newHeight, newWidth);
          const normalizedNewWidth = newWidth / Math.max(newHeight, newWidth);

          // Compare normalized dimensions to determine if new size is smaller
          if (
            normalizedNewHeight < normalizedOldHeight ||
            normalizedNewWidth < normalizedOldWidth
          ) {
            onIndexOfPointerOut(index);
          } else if (indexOfPointerOut > -1) {
            onIndexOfPointerOut(-1);
          }

          return result;
        });
      } else {
        clearAssetData();
      }
    } catch (error) {
      const result = { id, dimensions, error: true };
      if (assetPreviewFlagRef.current) {
        setHoveredAssetData(result);
      } else {
        clearAssetData();
      }
    }
  };

  const vqlSelectedLabelTexts = useMemo(
    () => selectedLabelTextsFromVql(vqlQueries),
    [vqlQueries],
  );

  const vqlSelectedUserTagTexts = useMemo(
    () => selectedUserTagTextsFromVql(vqlQueries, datasetUserTagsCatalog),
    [vqlQueries, datasetUserTagsCatalog],
  );

  const selectedLabels = useMemo(() => {
    if (cardMDType === MetadataType.USER_TAGS) {
      return vqlSelectedUserTagTexts;
    }
    return vqlSelectedLabelTexts;
  }, [cardMDType, vqlSelectedLabelTexts, vqlSelectedUserTagTexts]);

  const showExportButton =
    !!(isSelected || isHovering) &&
    showSelectOption &&
    !isVertex &&
    !hoveredAssetData.id;

  const headerCardType: CardTypes = isObjectModel
    ? "OBJECT"
    : enrichDatasetId && isSingleEntityOnDataPage
      ? singleData?.type.slice(0, singleData?.type?.length - 1)
      : singleData?.type;

  const isObject = [CardTypes.OBJECT, CardTypes.OBJECTS].includes(
    headerCardType,
  ); //different from isObjectCard - to be replaced in further refactors
  const selectedMdIcon = getMDIcon(cardMDType);
  const mdOptions = getMdOptions(isObject, isCaptionsVisible);

  const debugInfo = useMemo(() => {
    return getDebugInfoText(
      clusterID,
      clusters,
      showDebugInfo,
      singleData,
      isClusterPage,
    );
  }, [clusterID, clusters, showDebugInfo, singleData, isClusterPage]);

  const trackEntityOpenAction = (
    target: EventTarget,
    type: "Dataset" | "Cluster Details",
    clusterSize?: number,
  ) => {
    let properties: any = {
      [AMP_DATASET_EXPLORATION_PROPERTY__TARGET]: target,
      [AMP_DATASET_EXPLORATION_PROPERTY__CONTEXT]: type,
    };
    if (clusterSize) {
      properties = {
        ...properties,
        [AMP_DATASET_EXPLORATION_PROPERTY__CLUSTER_SIZE]: clusterSize,
      };
    }

    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__ENTITY__OPENED, properties);
  };

  const handleDetailsButtonClick = (e: any, data: Cluster) => {
    if (inReview && !!data.previews) {
      dispatch(setNavigationCluster(data));
    }
    if (data.onDetailsClick) {
      data.onDetailsClick(data);
      return;
    }

    if (e.target?.classList?.toString().includes("noDetailsClick")) {
      return;
    }

    if (isVertex) {
      return;
    }

    e.stopPropagation();
    clearAssetData();
    dispatch(clearImagePageParamsContext());

    const params = new URLSearchParams(location.search);

    if (isDataPage) {
      dispatch(setSimilarityData(null));
      dispatch(setNavigationCluster(null));
      dispatch(setDataPageScrollPosition(data.__id!));

      const imageCount = data?.previews?.length;
      if (imageCount === 1) {
        const firstPreview = data.previews[0];
        const isTypeImage = firstPreview.type === "IMAGE";
        const dynamicID = isTypeImage
          ? firstPreview.media_id
          : firstPreview.image_id;

        if (!data.flat_response) {
          dispatch(setNavigationCluster(data));
        }
        if (isVideoPageV2Enabled && firstPreview.video_id) {
          params.set(VIDEO_ID_PARAMETER, firstPreview.video_id);
        }

        let basePath = `${location.pathname}${data.flat_response ? `/cluster/${data.cluster_id}` : ""}/image/${dynamicID}?${params.toString()}`;
        if (isTypeImage) {
          navigate(basePath);
        } else {
          const newPath = addQueryParameter(
            basePath,
            OBJECT_ID_QUERY_PARAMETER,
            firstPreview.media_id,
          );
          navigate(newPath);
        }

        //track action when single entity on dataset exploration page
        const isVideo = !!firstPreview.video_uri;
        let target: EventTarget = "Image";
        if (isVideo) {
          target = "Video";
        } else if (isTypeImage) {
          target = "Image";
        } else if (firstPreview.type === "OBJECT") {
          target = "Object";
        }
        trackEntityOpenAction(target, "Dataset");
      } else {
        searchParams.set(
          DATA_PAGE_PARAMETER,
          searchParams.get(PAGE_PARAMETER) || "",
        );

        searchParams.delete(PAGE_PARAMETER);
        const newPath = `${location.pathname}/cluster/${clusterID}`;
        const params = `?${searchParams.toString()}`;
        navigate(newPath.concat(params));

        const clusterSize =
          singleData.type === "IMAGES"
            ? singleData.n_images
            : singleData.n_objects;
        trackEntityOpenAction("Cluster", "Dataset", clusterSize);
      }
    }

    if (isClusterPage) {
      dispatch(setClusterPageScrollPosition(singleData.__id));

      const dynamicID =
        singleData.type === "IMAGE" ? singleData.media_id : singleData.image_id;

      if (isVideoPageV2Enabled && singleData.video_id) {
        params.set(VIDEO_ID_PARAMETER, singleData.video_id);
        params.set(VIDEO_CLUSTER_ID_PARAMETER, clusterId!);
      }

      let imagePath = `${location.pathname}/image/${dynamicID}?${params.toString()}`;

      if (isSingleObject) {
        imagePath = addQueryParameter(
          imagePath,
          OBJECT_ID_QUERY_PARAMETER,
          singleData.media_id,
        );
      }

      dispatch(setSelectedCarouselIndex(index));
      //  TODO: fix
      navigate(imagePath);

      //track action when single entity on cluster details page
      const isVideo = !!singleData.video_uri;
      let target: EventTarget = "Image";

      if (isVideo) {
        target = "Video";
      } else if (singleData.type === "Image") {
        target = "Image";
      } else if (singleData.type === "OBJECT") {
        target = "Object";
      }
      trackEntityOpenAction(target, "Cluster Details");
    }
  };

  const handleSimilarButtonClick = () => {
    clearAssetData();
    if (onSetVertex) {
      if (singleData.flat_response || isClusterPage) {
        const preview = singleData.previews?.[0] || singleData;
        onSetVertex(preview.media_id, "MEDIA", false, preview);
      } else {
        onSetVertex(clusterID, "CLUSTER");
      }
    }
    dispatch(clearImagePageParamsContext());
    trackVisualSearch();
  };
  const handleExcludeButtonClick = () => {
    clearAssetData();
    if (onSetVertex) {
      if (singleData.flat_response || isClusterPage) {
        const preview = singleData.previews?.[0] || singleData;
        onSetVertex(preview.media_id, "MEDIA", true, preview);
      } else {
        onSetVertex(clusterID, "CLUSTER", true);
      }
    }
    dispatch(clearImagePageParamsContext());
    trackVisualSearch();
  };

  const handleVisualSimilarityApply = (threshold: string) => {
    if (onSetVertexSimilarity) {
      onSetVertexSimilarity(threshold);
    }
  };

  const handleAssetPreviewSetVertex = async (
    cropData?: any,
    negation?: boolean,
  ) => {
    clearAssetData();
    if (onSetVertex) {
      if (isSingleEntityOnDataPage) {
        await onSetVertex(clusterID, "CLUSTER", negation);
      } else {
        // For asset preview, we need to pass the asset data for upload
        const assetData = getAssetData();
        const mediaId = hoveredAssetData.id;

        if (mediaId && assetData && Object.keys(assetData).length > 0) {
          const assetDataWithCrop = cropData
            ? { ...assetData, cropData }
            : assetData;
          await onSetVertex(mediaId, "MEDIA", negation, assetDataWithCrop);
        } else if (mediaId) {
          // We have an ID but no asset data - use preview data as fallback
          const fallbackData =
            singleData.previews?.find((p: any) => p.media_id === mediaId) ||
            singleData.previews?.[0] ||
            singleData;
          await onSetVertex(mediaId, "MEDIA", negation, fallbackData);
        } else {
          // No hovered asset - use the first preview as default
          const defaultAssetData = isDataPage
            ? singleData.previews?.[0]
            : singleData;
          const defaultMediaId =
            defaultAssetData?.media_id || defaultAssetData?.image_id;

          if (defaultMediaId && defaultAssetData) {
            await onSetVertex(
              defaultMediaId,
              "MEDIA",
              negation,
              defaultAssetData,
            );
          }
        }
      }
    }
    dispatch(clearImagePageParamsContext());
    trackVisualSearch();
  };

  const trackVisualSearch = () => {
    let target: EventTarget = "Image";
    let clusterSize: number = 0;
    const isSingleEntity =
      singleData.previews && singleData.previews.length === 1;

    if (isSingleEntity) {
      const singlePreview = singleData.previews[0];
      const isVideo = !!singlePreview.video_uri;
      if (isVideo) target = "Video";
      else if (singlePreview.type === "IMAGE") target = "Image";
      else if (singlePreview.type === "OBJECT") target = "Object";
    } else if (singleData.type === "IMAGES") {
      target = "Cluster";
      clusterSize = singleData.n_images;
    } else if (singleData.type === "OBJECTS") {
      target = "Cluster";
      clusterSize = singleData.n_objects;
    }
    let properties: any = {
      [AMP_DATASET_EXPLORATION_PROPERTY__TARGET]: target,
    };
    if (!!clusterSize) {
      properties = {
        ...properties,
        [AMP_DATASET_EXPLORATION_PROPERTY__CLUSTER_SIZE]: clusterSize,
      };
    }
    //track visual search in amplitude(applied here)
    amplitudeTrack(
      AMP_DATASET_EXPLORATION_EVENT__VISUAL_SEARCH__APPLIED,
      properties,
    );
  };

  const handleMouseEnter = (e: any) => {
    e.stopPropagation();
    setIsHovering(true);
    setAssetPreviewFlag(true);
    if (indexOfPointerOut === index) {
      onIndexOfPointerOut(-1);
    }
  };
  const handleMouseLeave = (e: any) => {
    e.stopPropagation();
    setIsHovering(false);
    setAssetPreviewFlag(false);
  };

  const handleMDLabelSelection = useCallback(
    (value: string) => {
      const vqlMutators: VqlListMutators = {
        addVqlQuery,
        updateVqlQuery,
        removeVqlQuery,
      };

      switch (cardMDType) {
        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          return;
        case MetadataType.IMAGE_LABELS:
        case MetadataType.OBJECT_LABELS:
          toggleVqlLabelsIsValue(
            value,
            vqlSelectedLabelTexts.includes(value),
            vqlQueries,
            vqlMutators,
          );
          break;
        case MetadataType.USER_TAGS: {
          const tagId = resolveUserTagIdByName(value);
          toggleVqlUserTagsIsValue(
            tagId!,
            vqlHasUserTagInIsFilter(vqlQueries, tagId!),
            vqlQueries,
            vqlMutators,
          );
          break;
        }
      }
      onGlobalMDTypeChange(cardMDType);
    },
    [
      cardMDType,
      vqlQueries,
      vqlSelectedLabelTexts,
      addVqlQuery,
      updateVqlQuery,
      removeVqlQuery,
      resolveUserTagIdByName,
      onGlobalMDTypeChange,
    ],
  );

  const renderActionButtons = () => {
    return (
      <div className={styles.actionButtons}>
        {isVertex ? (
          isVisualSimilarityEnabled &&
          isSearchByRelevanceEnabled && (
            <VisualSimilarityPopover onApply={handleVisualSimilarityApply} />
          )
        ) : (
          <div className={styles.actionsWrapper}>
            {isVqlExcludeEnabled && (
              <Tooltip title="Exclude">
                <div
                  onClick={handleExcludeButtonClick}
                  className={classNames(
                    styles.actionButton,
                    styles.actionButtonExclude,
                    styles.noDetailsClick,
                  )}
                >
                  <ExcludeIcon />
                </div>
              </Tooltip>
            )}
            <Tooltip title="Find Similar">
              <div
                onClick={handleSimilarButtonClick}
                className={classNames(
                  styles.actionButton,
                  styles.noDetailsClick,
                )}
              >
                <VisualSimilarityIcon />
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const renderCardSelectionButtons = () => {
    if (
      disableInteractivity ||
      (searchParams.has(REVIEW_PARAMETER) &&
        !searchParams.has(LABEL_CLASS_PARAMETER))
    )
      return <noscript />;

    const wrapperClassNames = classNames(
      styles.noDetailsClick,
      styles.removeSimilarityBtn,
      styles.selectionButtonWrapper,
      {
        [styles.selectedWrapper]: isSelected || isVertex,
      },
    );

    if (isVertex) {
      return (
        <div onClick={onRemoveVertex} className={wrapperClassNames}>
          <div
            data-testid="remove-selection-button"
            className={styles.selectionButton}
          >
            <XIcon variant="new" />
          </div>
        </div>
      );
    } else if (showExportButton) {
      return (
        <div
          className={wrapperClassNames}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(e, isSelected, specificIndex, clusterID, singleData);
          }}
        >
          <div
            className={styles.selectionButton}
            data-testid="export-selection-button"
          >
            {isSelected && (
              <div
                data-testid="export-selected-button"
                className={styles.selection}
              >
                <CheckIcon />
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return <noscript />;
    }
  };

  const renderAssetPreview = () => {
    const assetData = getAssetData();
    const showCarousel =
      singleData.previews &&
      singleData.previews.length > 1 &&
      hoveredAssetData.id &&
      !hoveredAssetData.error;

    const enrichmentBoundingBoxes = isObjectOnEnrichmentPage
      ? getEnrichmentBoundingBoxes()
      : assetData.bounding_box
        ? [assetData.bounding_box]
        : undefined;

    return (
      <AssetPreviewCard
        uniqueClusterWrapperId={uniqueClusterWrapperId}
        indexOfPointerOut={indexOfPointerOut}
        hoveredAssetData={hoveredAssetData}
        selectedLabels={selectedLabels}
        selectedMdIcon={selectedMdIcon}
        showCarousel={showCarousel}
        containerRef={containerRef}
        isSelected={isSelected}
        singleData={singleData}
        assetData={assetData}
        debugInfo={debugInfo}
        titleIcon={titleIcon}
        mdOptions={mdOptions}
        isVertex={isVertex}
        title={finalTitle}
        index={index}
        key={index}
        clearAssetData={clearAssetData}
        onUpdateAssetData={updateAssetData}
        onSetAssetPreviewFlag={setAssetPreviewFlag}
        onSetAssetPreviewVertex={handleAssetPreviewSetVertex}
        //enrichment data
        mdLabels={mdLabels}
        cardMDType={cardMDType}
        enrichmentBoundingBoxes={enrichmentBoundingBoxes}
        onMDLabelSelection={handleMDLabelSelection}
        columnsCount={columnsCount}
        isVideoPageV2Enabled={isVideoPageV2Enabled}
      />
    );
  };

  useEffect(() => {
    const type = getInitialMDType(
      globalMDType,
      userTags,
      labels,
      captions,
      entityParams,
      hideMetadataOptions,
    );
    if (type !== cardMDType) {
      setCardMDType(type);
    }
    //eslint-disable-next-line
  }, [globalMDType]);
  const labelFromQueryParam = useMemo(
    () => JSON.parse(queryParamsFilters[LABELS_PARAMETER] || "[]"),
    [queryParamsFilters],
  );

  const getContextualLabels = useCallback(
    (state: unknown) => getDatasetContextualLabels(state, labelFromQueryParam),
    [labelFromQueryParam],
  );

  const contextualLabels = useSelector(getContextualLabels);
  const metadataSourceValue = queryParamsFilters[METADATA_SOURCES_PARAMETER];

  const getMdLabelsByType = (type: MetadataType) => {
    switch (type) {
      case MetadataType.IMAGE_LABELS:
      case MetadataType.OBJECT_LABELS:
        if (metadataSourceValue) {
          const metadataSources = JSON.parse(metadataSourceValue || "[]");
          if (metadataSources.length) {
            const relevantLabels = contextualLabels
              .filter(({ source }) =>
                metadataSources.some((s: string) =>
                  s.toLowerCase().startsWith(String(source).toLowerCase()),
                ),
              )
              .map(({ text }) => text);
            return intersection(labels, relevantLabels);
          }
        } else {
          return labels;
        }
        break;
      case MetadataType.OBJECT_CAPTIONS:
      case MetadataType.IMAGE_CAPTIONS:
        return captions;
      case MetadataType.USER_TAGS:
        return userTags;
    }
    return [];
  };

  useEffect(() => {
    let result: string[] = [];

    // Set labels based on isEnrichmentPage
    if (isEnrichmentPage) {
      result = getEnrichPageLabels();
    } else if (isDataCreationPage) {
      result = getCreationPageLabels();
    } else {
      result = getMdLabelsByType(cardMDType);
    }
    setMdLabels(result);
    //eslint-disable-next-line
  }, [
    metadataSourceValue,
    contextualLabels,
    width,
    showMetadataSummary,
    isEnrichmentPage,
    isDataCreationPage,
    globalMDType,
    cardMDType,
    hoveredAssetData,
    getMdLabelsByType,
  ]);

  const cardHeaderMap: Record<"default" | "review", JSX.Element> = {
    default: (
      <CardHeader
        suffix={
          singleData.is_pending ||
          (isClusterPage && navigationCluster?.is_pending)
            ? {
                icon: <ReloadIcon />,
                tooltipText: "New media – full indexing required",
              }
            : undefined
        }
        title={finalTitle}
        mdLabels={mdLabels}
        cardVariant={cardVariant}
        prefix={titleIcon}
        debugInfo={debugInfo}
        mdOptions={mdOptions}
        cardMdType={cardMDType}
        selectedLabels={selectedLabels}
        selectedMdIcon={selectedMdIcon}
        onMDLabelSelection={
          isBodyUnclickable ? undefined : handleMDLabelSelection
        }
      />
    ),
    review: (
      <ReviewCardHeader
        title={finalTitle as string}
        titleIcon={titleIcon}
        cardVariant={cardVariant}
        reviewClassLabel={singleData.label}
        floatingDetails={singleData.floatingDetails}
        isPreview={isPreview}
      />
    ),
  };

  const isTall = useMemo(() => {
    if (singleData.flat_response || isClusterPage || inReview) {
      const { width, height } = !isEmpty(singleData.previews)
        ? singleData.previews[0]
        : singleData;
      return height > width;
    }
    return false;
  }, [inReview, isClusterPage, singleData]);

  return (
    <EnrichmentCardProvider
      getMdLabelsByType={getMdLabelsByType}
      isSingleItem={plainImages.length <= 1}
    >
      <div
        ref={setNodeRef}
        id={uniqueClusterWrapperId}
        key={uniqueClusterWrapperId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          border: singleData.borderColor
            ? `${getVLColorByActionColor[singleData.borderColor as PreviewBorderColor]} 3px solid`
            : undefined,
        }}
        className={classNames(styles.singleClusterWrapper, {
          [styles.selected]: isSelected,
          [styles.tall]: isTall,
          [styles.reviewClusters]: inReview && !inReviewDetails,
          [styles.selectable]: selectable,
        })}
        onClick={(e: any) => {
          if (!hideMetadataOptions) {
            handleDetailsButtonClick(e, singleData);
          }
        }}
        {...listeners}
        {...attributes}
      >
        <div
          className={classNames(styles.extendedArea, styles.noDetailsClick)}
          onMouseEnter={() => setIsHovering(true)}
        ></div>
        <div className={cardClassNames}>
          {cardHeaderMap[inReview ? "review" : "default"]}
          {singleData.actionList && showFeedbackInCard && (
            <div className={styles.reviewActionWrapper}>
              <div className={styles.reviewActionListInner}>
                <VLFloatGroup
                  actionList={singleData.actionList}
                  placement="bottomRight"
                  tooltipPlacement="bottom"
                />
              </div>
            </div>
          )}

          {showFloatingDetails && (
            <div className={floatingDetailsClassnames}>
              <ReviewFloatingDetails
                floatingDetails={singleData.floatingDetails}
                className={classNames(styles.roundedFloatingDetails)}
              />
            </div>
          )}
          <CardBody
            opacity={singleData.opacity}
            isHovering={isHovering}
            plainImage={plainImage}
            thumbImage={thumbImage}
            hoverDelay={hoverDelay}
            mdLabels={mdLabels || []}
            plainImages={plainImages}
            thumbImages={thumbImages}
            boundingBox={boundingBox}
            unClickable={isBodyUnclickable}
            isSingleObject={isSingleObject}
            isAssetPreviewEnabled={dynamicAssetPreviewEnabled && !inReview}
            onUpdateAssetData={updateAssetData}
            inReview={inReview}
          />

          {renderCardSelectionButtons()}
          {showActionButtons && !inReview && renderActionButtons()}
        </div>
        {dynamicAssetPreviewEnabled && renderAssetPreview()}
      </div>
    </EnrichmentCardProvider>
  );
};

export default EnrichmentCard;
